import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Check, FileCheck, MoreHorizontal, Send, Undo2 } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Stat,
  StatGroup,
  StatusPill,
  type StatusTone,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import {
  useReconciliationCycle,
  useRunStatement,
  useRollbackStatement,
  formatVND,
  formatDateVN,
  type CycleStatus,
  type ServiceItem,
  type InvoiceItem,
} from '@/api/reconciliation';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { DetailHeader } from '@/components/detail-header';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { ReconciliationSendEmailDialog } from './reconciliation-send-email-dialog';
import { ReconciliationPipelineTab } from './reconciliation-pipeline-tab';

/* ── Tab config ──────────────────────────────────────────────────────────── */

const TABS = ['services', 'invoices', 'pipeline'] as const;
type TabValue = (typeof TABS)[number];
const DEFAULT_TAB: TabValue = 'services';

/* ── Status maps ─────────────────────────────────────────────────────────── */

const CYCLE_STATUS_LABEL: Record<CycleStatus, string> = {
  reconciling: 'Reconciling',
  done: 'Done',
};
const CYCLE_STATUS_TONE: Record<CycleStatus, StatusTone> = {
  reconciling: 'warning',
  done: 'success',
};

const SERVICE_STATUS_LABEL: Record<string, string> = {
  issued: 'Issued',
  pending: 'Pending',
};
const SERVICE_STATUS_TONE: Record<string, StatusTone> = {
  issued: 'success',
  pending: 'warning',
};

/** Maps invoice_status values to semantic StatusPill tones. */
function invoiceStatusTone(status: string): StatusTone {
  switch (status) {
    case 'paid':
      return 'success';
    case 'overdue':
      return 'danger';
    case 'waiting-confirm':
    case 'waiting_confirm':
      return 'warning';
    case 'approved':
      return 'info';
    case 'cancelled':
    case 'canceled':
      return 'neutral';
    case 'issued':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Humanize a raw invoice status token (e.g. "waiting-confirm" → "Waiting confirm"). */
function humanizeStatus(s: string): string {
  if (!s) return '—';
  const spaced = s.replace(/[-_]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/* ── Row action types ────────────────────────────────────────────────────── */

interface PendingAction {
  kind: 'run' | 'rollback';
  serviceId: number | string;
  serviceName: string;
}

/* ── Service tab columns (built as a factory so they get the action opener) */

function buildServiceColumns(
  onAction: (a: PendingAction) => void,
): ColumnDef<ServiceItem, unknown>[] {
  return [
    {
      id: 'service',
      header: 'Service',
      cell: ({ row }) => (
        <EntityLink
          to={`/services/${row.original.service_id}`}
          id={row.original.service_id}
          name={row.original.service_name}
        />
      ),
    },
    {
      id: 'product_group',
      header: 'Product Group',
      cell: ({ row }) => (
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          {row.original.product_group_name || '—'}
        </span>
      ),
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => (
        <EntityLink
          to={`/customers/${row.original.customer_id}`}
          id={row.original.customer_id}
          name={row.original.company_name || row.original.customer_name}
        />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const s = row.original.status;
        const tone = SERVICE_STATUS_TONE[s] ?? 'neutral';
        const label = SERVICE_STATUS_LABEL[s] ?? s;
        return <StatusPill tone={tone}>{label}</StatusPill>;
      },
    },
    {
      id: 'invoice',
      header: 'Invoice',
      cell: ({ row }) => {
        const { invoice_id, invoice_code } = row.original;
        if (!invoice_code) {
          return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
        }
        return (
          <TableLink to={`/invoices/${invoice_id}`}>{invoice_code}</TableLink>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const svc = row.original;
        const isIssued = svc.status === 'issued';
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Row actions">
                <MoreHorizontal size={15} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Run Statement: available when pending (no active snapshot yet) */}
              {!isIssued && (
                <DropdownMenuItem
                  onSelect={() =>
                    onAction({ kind: 'run', serviceId: svc.service_id, serviceName: svc.service_name })
                  }
                >
                  <FileCheck size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                  Run statement
                </DropdownMenuItem>
              )}
              {/* Rollback: only when issued (has_active_snapshot) */}
              {isIssued && (
                <DropdownMenuItem
                  onSelect={() =>
                    onAction({ kind: 'rollback', serviceId: svc.service_id, serviceName: svc.service_name })
                  }
                >
                  <Undo2 size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                  Rollback statement
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
}

/* ── Invoice tab columns ─────────────────────────────────────────────────── */

function buildInvoiceColumns(
  onSendSingle: (invoiceId: number) => void,
): ColumnDef<InvoiceItem, unknown>[] {
  return [
    {
      id: 'invoice_id',
      header: 'Invoice',
      cell: ({ row }) => {
        const { invoice_id, invoice_code } = row.original;
        return (
          <TableLink to={`/invoices/${invoice_id}`}>
            {invoice_code || String(invoice_id)}
          </TableLink>
        );
      },
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => (
        <EntityLink
          to={`/customers/${row.original.customer_id}`}
          id={row.original.customer_id}
          name={row.original.company_name || row.original.customer_name}
        />
      ),
    },
    {
      id: 'entity_service_name',
      header: 'Service',
      cell: ({ row }) => (
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          {row.original.entity_service_name || '—'}
        </span>
      ),
    },
    {
      id: 'invoice_amount',
      header: 'Amount (VND)',
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--vn-fg-default)' }}>
          {formatVND(row.original.invoice_amount)}
        </span>
      ),
    },
    {
      id: 'invoice_vat',
      header: 'VAT (VND)',
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--vn-fg-muted)' }}>
          {formatVND(row.original.invoice_vat)}
        </span>
      ),
    },
    {
      id: 'total_amount',
      header: 'Total (VND)',
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--vn-fg-default)' }}>
          {formatVND(row.original.invoice_total)}
        </span>
      ),
    },
    {
      id: 'invoice_type',
      header: 'Type',
      cell: ({ row }) => (
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          {row.original.invoice_type || '—'}
        </span>
      ),
    },
    {
      id: 'apply_period',
      header: 'Apply Period',
      cell: ({ row }) => {
        const { invoice_apply_startdate, invoice_apply_enddate } = row.original;
        if (!invoice_apply_startdate && !invoice_apply_enddate)
          return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
        return (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}>
            {formatDateVN(invoice_apply_startdate)} – {formatDateVN(invoice_apply_enddate)}
          </span>
        );
      },
    },
    {
      id: 'invoice_status',
      header: 'Status',
      cell: ({ row }) => {
        const s = row.original.invoice_status;
        return <StatusPill tone={invoiceStatusTone(s)}>{humanizeStatus(s)}</StatusPill>;
      },
    },
    {
      id: 'duedate',
      header: 'Due Date',
      cell: ({ row }) => (
        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {formatDateVN(row.original.invoice_duedate)}
        </span>
      ),
    },
    {
      id: 'mail_sent',
      header: 'Email Sent',
      cell: ({ row }) => {
        const sent = row.original.invoice_mail_count > 0;
        return sent ? (
          <StatusPill tone="success">
            <Check size={12} style={{ marginRight: 'var(--vn-space-1)' }} />
            Sent
          </StatusPill>
        ) : (
          <StatusPill tone="neutral">Not sent</StatusPill>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton variant="ghost" size="sm" aria-label="Row actions">
              <MoreHorizontal size={15} />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onSendSingle(row.original.invoice_id)}>
              <Send size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
              Send email
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];
}

/* ── Page ────────────────────────────────────────────────────────────────── */

/**
 * /reconciliation/:cohortKey — detail of one reconciliation cycle.
 *
 * Layout: grid gap-4 →
 *   DetailHeader (breadcrumb + period + status + Send email)
 *   Summary stat band (5 metrics)
 *   Tabs [Services | Invoices]
 *     Services: service, product_group, customer, status, invoice, ⋯ actions (Run/Rollback)
 *     Invoices: invoice, customer, service, amount, vat, total, type, period, status, mail, ⋯ (Send)
 *
 * Run Statement / Rollback: USER must confirm the ConfirmDialog — never fired automatically.
 */
export default function ReconciliationDetailPage() {
  const { cohortKey = '' } = useParams<{ cohortKey: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sendOpen, setSendOpen] = useState(false);
  const [singleInvoiceId, setSingleInvoiceId] = useState<number | null>(null);

  // Service row actions
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const runStatement = useRunStatement();
  const rollbackStatement = useRollbackStatement();

  const rawTab = searchParams.get('tab') ?? '';
  const activeTab: TabValue = (TABS as readonly string[]).includes(rawTab)
    ? (rawTab as TabValue)
    : DEFAULT_TAB;

  const setTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const { data, isLoading, isError, refetch } = useReconciliationCycle(cohortKey);

  const handleServiceAction = (action: PendingAction) => {
    setPendingAction(action);
    setActionError(null);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    setActionError(null);
    try {
      if (pendingAction.kind === 'run') {
        await runStatement.mutateAsync(pendingAction.serviceId);
      } else {
        await rollbackStatement.mutateAsync(pendingAction.serviceId);
      }
      setPendingAction(null);
      // Refresh the cycle detail to reflect new status
      refetch();
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { message?: string; error?: string } };
        message?: string;
      };
      const msg =
        axiosError?.response?.data?.message ||
        axiosError?.response?.data?.error ||
        axiosError?.message ||
        'An unexpected error occurred.';
      setActionError(msg);
    }
  };

  const handleOpenSingleSend = (invoiceId: number) => {
    setSingleInvoiceId(invoiceId);
    setSendOpen(true);
  };

  const handleSendOpenChange = (open: boolean) => {
    setSendOpen(open);
    if (!open) setSingleInvoiceId(null);
  };

  /* ── Full-page guards ─────────────────────────────────────────────────── */
  if (isError) {
    return (
      <Container size="full">
        <TabError title="Unable to load reconciliation cycle" onRetry={() => refetch()} />
      </Container>
    );
  }
  if (isLoading || !data) {
    return (
      <Container size="full">
        <TabLoading rows={4} />
      </Container>
    );
  }

  // cohort_key is now an opaque UUID; derive the period label from the payload dates.
  const periodLabel = `${formatDateVN(data.period_startdate)} – ${formatDateVN(data.period_enddate)}`;
  const cycleTone = CYCLE_STATUS_TONE[data.status] ?? 'neutral';
  const cycleLabel = CYCLE_STATUS_LABEL[data.status] ?? data.status;

  /* ── Summary stats ────────────────────────────────────────────────────── */
  const stats = [
    { label: 'Total Services', value: (data.issued_count + data.pending_count).toLocaleString('vi-VN'), dotColor: 'var(--vn-primary-solid)' },
    { label: 'Issued', value: data.issued_count.toLocaleString('vi-VN'), dotColor: 'var(--vn-success-solid)' },
    { label: 'Pending', value: data.pending_count.toLocaleString('vi-VN'), dotColor: 'var(--vn-warning-solid)' },
    { label: 'Total Amount', value: formatVND(data.total_amount), dotColor: 'var(--vn-info-solid)' },
    { label: 'Emails Sent', value: data.mail_sent_count.toLocaleString('vi-VN'), dotColor: 'var(--vn-fg-muted)' },
  ];

  const isActionPending = runStatement.isPending || rollbackStatement.isPending;
  const serviceColumns = buildServiceColumns(handleServiceAction);
  const invoiceColumns = buildInvoiceColumns(handleOpenSingleSend);

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* ── Identity header ── */}
        <DetailHeader
          parent={{ label: 'Reconciliation', to: '/reconciliation' }}
          current={`Period ${periodLabel}`}
          title={periodLabel}
          meta={<StatusPill tone={cycleTone}>{cycleLabel}</StatusPill>}
          actions={
            <Button
              startIcon={<Send size={14} />}
              onClick={() => setSendOpen(true)}
            >
              Send email
            </Button>
          }
        />

        {/* ── Summary stat band ── */}
        <Card flat>
          <CardBody>
            <StatGroup columns={5}>
              {stats.map((s) => (
                <Stat key={s.label} label={s.label} value={s.value} dotColor={s.dotColor} />
              ))}
            </StatGroup>
          </CardBody>
        </Card>

        {/* ── Tabs ── */}
        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="services">
              Services ({data.services.length})
            </TabsTrigger>
            <TabsTrigger value="invoices">
              Invoices ({data.invoices.length})
            </TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          </TabsList>

          <TabsContent value="services">
            {activeTab === 'services' && (
              data.services.length === 0 ? (
                <EmptyState title="No services" description="This cycle has no service data." />
              ) : (
                <DataTable
                  columns={serviceColumns}
                  data={data.services}
                  getRowId={(r) => String(r.service_id)}
                />
              )
            )}
          </TabsContent>

          <TabsContent value="invoices">
            {activeTab === 'invoices' && (
              data.invoices.length === 0 ? (
                <EmptyState title="No invoices" description="This cycle has no invoice data." />
              ) : (
                <DataTable
                  columns={invoiceColumns}
                  data={data.invoices}
                  getRowId={(r) => String(r.invoice_id)}
                />
              )
            )}
          </TabsContent>

          <TabsContent value="pipeline">
            {activeTab === 'pipeline' && <ReconciliationPipelineTab cohortKey={cohortKey} />}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Send email dialog (all-cycle or single-invoice) ── */}
      <ReconciliationSendEmailDialog
        open={sendOpen}
        onOpenChange={handleSendOpenChange}
        cohortKey={cohortKey}
        periodLabel={periodLabel}
        singleInvoiceId={singleInvoiceId ?? undefined}
      />

      {/* ── Run statement / Rollback confirm dialog ── */}
      {pendingAction && (
        <ConfirmDialog
          open={!!pendingAction}
          onOpenChange={(o) => {
            if (!o) {
              setPendingAction(null);
              setActionError(null);
            }
          }}
          title={
            pendingAction.kind === 'run'
              ? 'Run statement'
              : 'Rollback statement'
          }
          description={
            pendingAction.kind === 'run'
              ? `Run a statement invoice for "${pendingAction.serviceName}"? This will create a new statement invoice for this service.`
              : `Roll back the statement for "${pendingAction.serviceName}"? This will cancel the statement invoice and restore the service billing cycle.`
          }
          destructive={pendingAction.kind === 'rollback'}
          confirmLabel={pendingAction.kind === 'run' ? 'Run statement' : 'Rollback'}
          loading={isActionPending}
          onConfirm={handleConfirmAction}
        >
          {actionError && (
            <Alert tone="danger" title="Action failed">
              {actionError}
            </Alert>
          )}
        </ConfirmDialog>
      )}
    </Container>
  );
}
